import React, { useState, useEffect, useContext } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Stack,
  Typography,
  Chip,
  useTheme
} from "@mui/material"
import PersonIcon from "@mui/icons-material/PersonOutlined"
import { EditMemberContactDetailsTab } from "./EditMemberContactDetailsTab"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import {
  CommunityMembershipRequest,
  getCompanyMember,
  updateMember,
  UpdateMemberRequest,
  type CompanyProfile
} from "src/services/api/companyMembers"
import { getGatedCommunityMember, type GatedCommunityProfileStatus } from "src/services/api/gatedCommunities"
import { updateUserCommunities, resolveKnowledgeBaseName } from "src/services/api/user"
import {
  EditMemberCommunitiesTab,
  CommunityItem,
  type AddableCommunity
} from "./EditMemberCommunitiesTab"
import { useCompany, UserContext, useSnackbar, useUser, useCommunities } from "src/providers"
import { getRoleLabel, Role, ROLE_LABELS, UserRole } from "src/UserService"
import { Member } from "./MembersTable"
import { StatusChip } from "src/components/table/StatusChip"

const isBlank = (s: string | null | undefined) => !s || !String(s).trim()

const COMPANY_MEMBERS_HIDDEN_ROLES: Role[] = [
  UserRole.PLAIN,
  UserRole.EXFLUENCY_ADMIN,
  UserRole.PLATFORM_MANAGER,
  UserRole.SUPPORT,
  UserRole.GATED_COMMUNITY_MANAGER,
  UserRole.GC_PROJECT_MANAGER
]

const editMemberRoleOptions: { id: Role; label: string }[] = (Object.keys(ROLE_LABELS) as UserRole[])
  .filter((id) => !COMPANY_MEMBERS_HIDDEN_ROLES.includes(id as Role))
  .map((id) => ({ id: id as Role, label: ROLE_LABELS[id] }))

interface EditMemberModalProps {
  open: boolean
  member: Member
  communityId?: string
  onClose: () => void
  onUpdated: () => void
}

export const EditMemberModal = ({ open, member, communityId, onClose, onUpdated }: EditMemberModalProps) => {
  const { userCommunities } = useContext(UserContext)
  const user = useUser()
  const { snackbarShowMessage } = useSnackbar()
  const { isOrgAdmin, companyId } = useCompany()
  const { managerCommunities } = useCommunities()
  const managerCommunityIds = new Set(managerCommunities.map((c) => c.gatedCommunityId))
  const { palette } = useTheme()
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [profile, setProfile] = useState<CompanyProfile | undefined>()
  const [roles, setRoles] = useState<Role[]>([])
  const [communityItems, setCommunityItems] = useState<CommunityItem[]>([])
  const [toAddMap, setToAddMap] = useState<Record<string, GatedCommunityProfileStatus>>({})
  const [contactDetailsReadOnly, setContactDetailsReadOnly] = useState(false)

  useEffect(() => {
    if (!member || !member.userId || !companyId) return
    setLoading(true)
    setTab(0)
    setToAddMap({})
    setContactDetailsReadOnly(false)
    getCompanyMember(companyId, member.userId)
      .then((fetched) => {
        setFirstName(fetched.firstName)
        setLastName(fetched.lastName)
        setEmail(fetched.email)
        setPhone(fetched.phoneNumber ?? "")
        setProfile(fetched.profile ?? undefined)
        setRoles(fetched.roles.filter((r) => !COMPANY_MEMBERS_HIDDEN_ROLES.includes(r)))
        const email = fetched.email
        setCommunityItems(fetched.communities.map((c) => ({
          ...c,
          communityName: resolveKnowledgeBaseName(c.communityName, user.login),
          toRemove: false
        })))
      })
      .catch((err) => {
        console.log("err", err)
        if (err?.status === 404 && communityId) {
          setContactDetailsReadOnly(true)
          return getGatedCommunityMember(communityId, member.userId!)
            .then((fetched) => {
              setFirstName(fetched.firstName ?? "")
              setLastName(fetched.lastName ?? "")
              setEmail(fetched.email ?? "")
              setPhone(fetched.phoneNumber ?? "")
              setProfile(undefined)
              setRoles(fetched.roles.filter((r) => !COMPANY_MEMBERS_HIDDEN_ROLES.includes(r)))
              const email = fetched.email ?? ""
              setCommunityItems(fetched.communities.map((c) => ({
                ...c,
                communityName: resolveKnowledgeBaseName(c.communityName, user.login),
                communityProfile: c.profile,
                toRemove: false
              })))
            })
            .catch(() => snackbarShowMessage("Failed to load member data", "error"))
        }
        snackbarShowMessage("Failed to load member data", "error")
      })
      .finally(() => setLoading(false))
  }, [member, communityId])

  const errors = (!isOrgAdmin || contactDetailsReadOnly) ? { firstName: false, lastName: false, email: false } : {
    firstName: isBlank(firstName),
    lastName: isBlank(lastName),
    email: isBlank(email)
  }
  const updateDisabled = Object.values(errors).some(Boolean)

  const handleRemoveCommunity = (communityId: string) => {
    setCommunityItems((prev) =>
      prev.map((c) => (c.communityId === communityId ? { ...c, toRemove: true } : c))
    )
  }

  const handleUndoRemoveCommunity = (communityId: string) => {
    setCommunityItems((prev) =>
      prev.map((c) => (c.communityId === communityId ? { ...c, toRemove: false } : c))
    )
  }

  const handleCommunityProfileChange = (communityId: string, newProfile: GatedCommunityProfileStatus) => {
    setCommunityItems((prev) =>
      prev.map((c) => (c.communityId === communityId ? { ...c, communityProfile: newProfile } : c))
    )
  }

  const memberCommunityIds = new Set(communityItems.map((c) => c.communityId))
  const addableCommunities: AddableCommunity[] = (userCommunities ?? [])
    .filter((c) => c.profile === "GATED_COMMUNITY_ADMIN" && !memberCommunityIds.has(c.gatedCommunityId) && c.gatedCommunityName !== "My Knowledge Base")
    .map((c) => ({
      id: c.gatedCommunityId,
      name: c.gatedCommunityName,
      toAdd: c.gatedCommunityId in toAddMap,
      profile: toAddMap[c.gatedCommunityId] ?? "GATED_COMMUNITY_MEMBER"
    }))

  const handleAddCommunity = (id: string) => {
    setToAddMap((prev) => ({ ...prev, [id]: "GATED_COMMUNITY_MEMBER" }))
  }

  const handleUndoAddCommunity = (id: string) => {
    setToAddMap((prev) => { const next = { ...prev }; delete next[id]; return next })
  }

  const handleToAddProfileChange = (id: string, newProfile: GatedCommunityProfileStatus) => {
    setToAddMap((prev) => ({ ...prev, [id]: newProfile }))
  }

  const handleUpdate = () => {
    if (!member || updateDisabled || !member.userId) return
    if (communityId) {
      const communities = [
        ...communityItems
          .filter((c) => !c.toRemove)
          .map((c) => ({ communityId: c.communityId, profile: c.communityProfile })),
        ...addableCommunities.filter((c) => c.toAdd).map((c) => ({ communityId: c.id, profile: c.profile }))
      ]
      Promise.all([
        updateUserCommunities(member.userId, communities)
      ])
        .then(() => {
          snackbarShowMessage("Member Knowledge Bases updated", "success")
          onUpdated()
          onClose()
        })
        .catch(() => snackbarShowMessage("Failed to update member", "error"))
      return
    }
    if (!companyId) return
    const communities: CommunityMembershipRequest[] = [
      ...communityItems
        .filter((c) => !c.toRemove)
        .map((c) => ({
          communityId: c.communityId,
          companyProfile: (c.communityProfile === "GATED_COMMUNITY_ADMIN" ? "MANAGER" : "MEMBER") as CompanyProfile
        })),
      ...addableCommunities.filter((c) => c.toAdd).map((c) => ({
        communityId: c.id,
        companyProfile: (c.profile === "GATED_COMMUNITY_ADMIN" ? "MANAGER" : "MEMBER") as CompanyProfile
      }))
    ]
    const request: UpdateMemberRequest = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      companyProfile: profile ?? "MEMBER",
      roles,
      communities,
      phoneNumber: phone.trim() || undefined
    }
    updateMember(companyId, member.userId, request)
      .then(() => {
        snackbarShowMessage("Member updated", "success")
        onUpdated()
        onClose()
      })
      .catch(() => snackbarShowMessage("Failed to update member", "error"))
  }

  if (loading) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{
      sx: { borderRadius: "25px", pt: 4, px: 4, minHeight: "600px" }
    }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: "32px", fontWeight: 600, px: 0 }}>
        <PersonIcon sx={{ fontSize: 36 }} />
        Edit Member
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          "& .MuiTab-root": {
            backgroundColor: "#EEF0F3",
            color: palette.text.primary,
            textTransform: "none", fontWeight: 600, opacity: 0.4, width: "33.33%",
            borderRadius: "15px 15px 0px 0px"
          },
          "& .MuiTab-root.Mui-selected": { opacity: 1, borderBottom: "none", color: palette.text.primary },
          "& .MuiTabs-indicator": { backgroundColor: palette.common.white, height: "1px" }
        }}
      >
        <Tab label="Contact details" />
        <Tab label="Knowledge Bases" />
        {!contactDetailsReadOnly && <Tab label="Roles" />}
      </Tabs>
      <DialogContent sx={{ px: 3, py: 4, backgroundColor: "#EEF0F3", borderRadius: "0px 0px 15px 15px" }}>
        {tab === 0 && (
          <EditMemberContactDetailsTab
            firstName={firstName}
            lastName={lastName}
            email={email}
            phoneNumber={phone}
            profile={profile}
            errors={errors}
            readOnly={!isOrgAdmin || contactDetailsReadOnly}
            isSelf={member.email === user?.email}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            onEmailChange={setEmail}
            onPhoneNumberChange={setPhone}
            onProfileChange={setProfile}
          />
        )}
        {tab === 1 && (
          <EditMemberCommunitiesTab
            communityItems={communityItems}
            addableCommunities={addableCommunities}
            managerCommunityIds={managerCommunityIds}
            onRemove={handleRemoveCommunity}
            onUndo={handleUndoRemoveCommunity}
            onProfileChange={handleCommunityProfileChange}
            onAdd={handleAddCommunity}
            onUndoAdd={handleUndoAddCommunity}
            onToAddProfileChange={handleToAddProfileChange}
          />
        )}
        {tab === 2 && (
          <Stack spacing={3} sx={{ px: 4 }}>
            <Stack spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Active role/s</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {roles.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No roles assigned</Typography>
                )}
                {roles.map((role) => (
                  <StatusChip
                    key={role}
                    label={getRoleLabel(role)}
                    onDelete={() => setRoles((prev) => prev.filter((r) => r !== role))}
                    sx={{ backgroundColor: "#CBE8F6", fontWeight: 600, textTransform: "uppercase" }}
                  />
                ))}
              </Stack>
            </Stack>
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Add new role/s</Typography>
              <CustomAutocomplete<{ id: Role; label: string }>
                label="Roles"
                options={editMemberRoleOptions.filter((r) => !roles.includes(r.id))}
                value={null}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, val) => {
                  if (val && !Array.isArray(val)) {
                    setRoles((prev) => [...prev, (val as { id: Role; label: string }).id])
                  }
                }}
                size="small"
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 0, pb: 2, pt: 3, gap: 0.5 }}>
        <Button variant="contained" color="primary" onClick={handleUpdate} disabled={updateDisabled}>
          Update
        </Button>
        <Button variant="outlined" color="secondary" onClick={onClose} >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  )
}
